import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  TrophyIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { CalendarIcon as CalendarIconSolid } from '@heroicons/react/24/solid';

const ScheduleManagement = () => {
  const { sportId } = useParams();
  const [sport, setSport] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'practice',
    startDate: '',
    startTime: '',
    location: '',
    opponent: ''
  });

  useEffect(() => {
    fetchSportData();
    fetchSchedules();
  }, [sportId]);

  const fetchSportData = async () => {
    try {
      const response = await fetch(`/api/sports/id/${sportId}`);
      if (response.ok) {
        const data = await response.json();
        setSport(data.sport);
      } else {
        // Mock data fallback
        setSport({
          id: sportId,
          name: 'Football',
          slug: 'football',
          coachName: 'Coach Johnson'
        });
      }
    } catch (error) {
      console.error('Error fetching sport:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/schedules/sport/${sportId}`);
      if (response.ok) {
        const data = await response.json();
        setSchedules(data.schedules || []);
      } else {
        // Mock data fallback
        loadMockSchedules();
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      loadMockSchedules();
    } finally {
      setLoading(false);
    }
  };

  const loadMockSchedules = () => {
    const mockSchedules = [
      {
        id: 1,
        title: 'vs. Central High',
        description: 'Home game against Central High School',
        type: 'game',
        startDate: '2024-02-15',
        startTime: '19:00',
        endTime: '21:00',
        location: 'East End Prep Stadium',
        opponent: 'Central High',
        isHome: true,
        status: 'scheduled',
        priority: 'high',
        isPublic: true,
        transportation: 'N/A - Home Game',
        equipment: 'Full game equipment required',
        notes: 'Senior night celebration before the game'
      },
      {
        id: 2,
        title: 'Team Practice',
        description: 'Regular team practice session',
        type: 'practice',
        startDate: '2024-02-12',
        startTime: '15:30',
        endTime: '17:30',
        location: 'Practice Field',
        opponent: '',
        isHome: true,
        status: 'scheduled',
        priority: 'medium',
        isPublic: false,
        transportation: '',
        equipment: 'Practice gear',
        notes: 'Focus on offensive plays'
      },
      {
        id: 3,
        title: 'at West High',
        description: 'Away game at West High School',
        type: 'game',
        startDate: '2024-02-22',
        startTime: '18:00',
        endTime: '20:00',
        location: 'West High Stadium',
        opponent: 'West High',
        isHome: false,
        status: 'scheduled',
        priority: 'high',
        isPublic: true,
        transportation: 'Bus departs at 4:30 PM',
        equipment: 'Away uniforms',
        notes: 'Rivalry game - expect large crowd'
      },
      {
        id: 4,
        title: 'Film Study',
        description: 'Video analysis session',
        type: 'meeting',
        startDate: '2024-02-14',
        startTime: '16:00',
        endTime: '17:00',
        location: 'Team Room',
        opponent: '',
        isHome: true,
        status: 'scheduled',
        priority: 'medium',
        isPublic: false,
        transportation: '',
        equipment: '',
        notes: 'Review Central High game footage'
      }
    ];
    setSchedules(mockSchedules);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted!');
    
    // Basic validation
    if (!formData.title || !formData.startDate || !formData.startTime) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      const scheduleData = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        startDate: formData.startDate,
        startTime: formData.startTime,
        location: formData.location,
        opponent: formData.opponent,
        sportId: sportId
      };
      
      console.log('Schedule data to send:', scheduleData);
      console.log('Sport ID:', sportId);
      
      const url = editingSchedule ? `/api/schedules/${editingSchedule.id}` : '/api/schedules';
      const method = editingSchedule ? 'PUT' : 'POST';
      
      console.log('Making request to:', url, 'with method:', method);
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(scheduleData)
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        alert(editingSchedule ? 'Event updated successfully!' : 'Event created successfully!');
        setShowAddModal(false);
        setEditingSchedule(null);
        resetForm();
        fetchSchedules();
      } else {
        const errorData = await response.json();
        console.log('Error response:', errorData);
        alert(`Error: ${errorData.message || 'Failed to save event'}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Network error: ' + error.message);
    }
  };
  
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'practice',
      startDate: '',
      startTime: '',
      location: '',
      opponent: ''
    });
  };

  // Helper functions to format date and time for form inputs
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Extract date parts directly from the ISO string to avoid timezone issues
    const isoString = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    return isoString; // Should already be in YYYY-MM-DD format
  };

  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    // Handle both "HH:MM:SS" and "HH:MM" formats
    return timeString.substring(0, 5); // Returns HH:MM format
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      title: schedule.title || '',
      description: schedule.description || '',
      type: schedule.type || 'practice',
      startDate: formatDateForInput(schedule.startDate),
      startTime: formatTimeForInput(schedule.startTime),
      location: schedule.location || '',
      opponent: schedule.opponent || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (scheduleId) => {
    if (window.confirm('Are you sure you want to delete this schedule item?')) {
      try {
        const response = await fetch(`/api/schedules/${scheduleId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (response.ok) {
          await fetchSchedules();
        } else {
          console.error('Error deleting schedule');
        }
      } catch (error) {
        console.error('Error deleting schedule:', error);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Extract date parts directly from the ISO string to avoid timezone issues
    const isoString = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const [year, month, day] = isoString.split('-');
    const date = new Date(year, month - 1, day); // Create date in local timezone
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'game':
        return TrophyIcon;
      case 'practice':
        return UserGroupIcon;
      case 'meeting':
        return CalendarIcon;
      default:
        return CalendarIcon;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'game':
        return 'text-blue-600 bg-blue-100';
      case 'practice':
        return 'text-green-600 bg-green-100';
      case 'meeting':
        return 'text-purple-600 bg-purple-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredSchedules = schedules.filter(schedule => {
    const matchesSearch = 
      schedule.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (schedule.opponent && schedule.opponent.toLowerCase().includes(searchTerm.toLowerCase())) ||
      schedule.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !filterType || schedule.type === filterType;
    
    const matchesMonth = !filterMonth || 
      new Date(schedule.startDate).getMonth() === parseInt(filterMonth);
    
    return matchesSearch && matchesType && matchesMonth;
  });

  const sortedSchedules = filteredSchedules.sort((a, b) => 
    new Date(a.startDate + 'T' + a.startTime) - new Date(b.startDate + 'T' + b.startTime)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/admin/sports"
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {sport?.name} Schedule Management
              </h1>
              <p className="text-gray-600 mt-1">
                Manage practices, games, and team events
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingSchedule(null);
              setShowAddModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Event
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="game">Games</option>
            <option value="practice">Practices</option>
            <option value="meeting">Meetings</option>
          </select>
          
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">All Months</option>
            <option value="0">January</option>
            <option value="1">February</option>
            <option value="2">March</option>
            <option value="3">April</option>
            <option value="4">May</option>
            <option value="5">June</option>
            <option value="6">July</option>
            <option value="7">August</option>
            <option value="8">September</option>
            <option value="9">October</option>
            <option value="10">November</option>
            <option value="11">December</option>
          </select>
          
          <div className="flex items-center text-sm text-gray-600">
            <FunnelIcon className="h-5 w-5 mr-2" />
            {sortedSchedules.length} of {schedules.length} events
          </div>
        </div>
      </div>

      {/* Schedule List */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Schedule</h2>
        </div>
        
        {sortedSchedules.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {sortedSchedules.map((schedule) => {
              const TypeIcon = getTypeIcon(schedule.type);
              return (
                <div key={schedule.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-full ${getTypeColor(schedule.type)}`}>
                        <TypeIcon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {schedule.title}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(schedule.type)}`}>
                            {schedule.type.charAt(0).toUpperCase() + schedule.type.slice(1)}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(schedule.priority)}`}>
                            {schedule.priority.charAt(0).toUpperCase() + schedule.priority.slice(1)} Priority
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-6 text-sm text-gray-600 mt-2">
                          <span className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {formatDate(schedule.startDate)}
                          </span>
                          <span className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {formatTime(schedule.startTime)}
                            {schedule.endTime && ` - ${formatTime(schedule.endTime)}`}
                          </span>
                          {schedule.location && (
                            <span className="flex items-center">
                              <MapPinIcon className="h-4 w-4 mr-1" />
                              {schedule.location}
                            </span>
                          )}
                          {schedule.type === 'game' && schedule.opponent && (
                            <span className="font-medium">
                              {schedule.isHome ? 'vs.' : 'at'} {schedule.opponent}
                            </span>
                          )}
                        </div>
                        
                        {schedule.description && (
                          <p className="text-sm text-gray-600 mt-2">{schedule.description}</p>
                        )}
                        
                        {(schedule.transportation || schedule.equipment || schedule.notes) && (
                          <div className="mt-3 space-y-1">
                            {schedule.transportation && (
                              <p className="text-xs text-gray-500">
                                <strong>Transportation:</strong> {schedule.transportation}
                              </p>
                            )}
                            {schedule.equipment && (
                              <p className="text-xs text-gray-500">
                                <strong>Equipment:</strong> {schedule.equipment}
                              </p>
                            )}
                            {schedule.notes && (
                              <p className="text-xs text-gray-500">
                                <strong>Notes:</strong> {schedule.notes}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!schedule.isPublic && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Private
                        </span>
                      )}
                      <button
                        onClick={() => handleEdit(schedule)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(schedule.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <CalendarIconSolid className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No events found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Add/Edit Schedule Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingSchedule ? 'Edit Event' : 'Add New Event'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingSchedule(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="practice">Practice</option>
                      <option value="game">Game</option>
                      <option value="meeting">Meeting</option>
                      <option value="tournament">Tournament</option>
                      <option value="scrimmage">Scrimmage</option>
                    </select>
                  </div>
                    
                    {formData.type === 'game' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Opponent</label>
                        <input
                          type="text"
                          name="opponent"
                          value={formData.opponent}
                          onChange={handleInputChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Start Date *</label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Start Time *</label>
                      <input
                        type="time"
                        name="startTime"
                        value={formData.startTime}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Location</label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                
                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingSchedule(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {editingSchedule ? 'Update Event' : 'Add Event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleManagement;